import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock,
  ExternalLink,
  FileCheck2,
  Globe2,
  GraduationCap,
  Languages,
  MapPin,
  MessageCircle,
  Quote,
  RotateCw,
  Search,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserCheck,
  UsersRound,
  Zap,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import {
  BLACK_PROFESSIONALS_IMAGE,
  AFRICAN_TALENTS_HERO_IMAGE,
  CANADA_MAPLE_IMAGE,
  PROFESSIONALS_IMAGE,
  TORONTO_WATERFRONT_IMAGE,
} from "../lib/brand";

const IRCC_DRAWS_URL =
  "https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/rounds-invitations.html";

const IRCC_CRS_TOOL_URL_FR =
  "https://ircc.canada.ca/francais/immigrer/qualifie/scg-outil.asp";
const IRCC_CRS_TOOL_URL_EN =
  "https://ircc.canada.ca/english/immigrate/skilled/crs-tool.asp";

// Up-to-date recent Express Entry draws based on official IRCC rounds
const FALLBACK_DRAWS = [
  {
    draw_number: 366,
    date: "2026-07-22",
    category: "French language proficiency",
    category_fr: "Compétences linguistiques en français",
    crs: 399,
    itas: 5000,
    isFrench: true,
  },
  {
    draw_number: 365,
    date: "2026-07-09",
    category: "French language proficiency",
    category_fr: "Compétences linguistiques en français",
    crs: 420,
    itas: 5000,
    isFrench: true,
  },
  {
    draw_number: 364,
    date: "2026-06-24",
    category: "Healthcare occupations",
    category_fr: "Professions de la santé",
    crs: 465,
    itas: 4000,
    isFrench: false,
  },
  {
    draw_number: 363,
    date: "2026-06-12",
    category: "STEM occupations",
    category_fr: "Professions STIM",
    crs: 491,
    itas: 2500,
    isFrench: false,
  },
  {
    draw_number: 362,
    date: "2026-03-18",
    category: "French language proficiency",
    category_fr: "Compétences linguistiques en français",
    crs: 393,
    itas: 4000,
    isFrench: true,
  },
];

const content = {
  fr: {
    badgeTop: "🇨🇦 Entrée Express · Catégorie Prioritaire Francophone",
    eyebrow: "Immigration canadienne pour les talents francophones",
    title1: "Diplômé(e) ?",
    title2: "Tu parles français ?",
    title3: "Le Canada te cherche.",
    sub: "Découvre les programmes d’immigration, d’études et de travail adaptés à ton profil — où que tu vives — avec une information vérifiée, une méthode structurée et un accompagnement humain sans fausses promesses.",
    primary: "Faire mon diagnostic TCF gratuit",
    secondary: "Explorer les 7 programmes",
    badge: "Information vérifiable • Accompagnement à distance",
    trust: "Du monde francophone au Canada, avec une démarche compréhensible, transparente et sans promesses irréalistes.",
    
    // Pathway finder widget
    finderTitle: "Trouve ta voie vers le Canada en 2 clics",
    finderSubtitle: "Sélectionne ton profil actuel et ton objectif :",
    finderProfileLabel: "1. Ton profil :",
    finderGoalLabel: "2. Ton objectif :",
    finderResultLabel: "Parcours recommandé :",
    finderAdvantageLabel: "Avantage stratégique :",
    
    servicesKicker: "Programmes & Services JNIC",
    servicesTitle: "Commence par la voie qui correspond à ton projet",
    servicesSub: "Une sélection claire des parcours les plus sollicités : résidence permanente, préparation TCF, études supérieures et mobilité professionnelle.",
    
    resultsKicker: "Résultats & Témoignages",
    resultsTitle: "Des parcours réels, accompagnés avec méthode",
    resultsSub: "Ces chiffres reflètent l’expérience concrète de JNIC. Chaque dossier reste unique et traité avec rigueur.",
    
    drawsKicker: "Entrée Express · Analyse des Seuils IRCC",
    drawsTitle: "Pourquoi le français est la clé absolue ?",
    drawsSub: "Compare les seuils CRS récents : la catégorie francophone offre un avantage décisif avec des scores accessibles (jusqu'à 110 points de moins que le tirage général).",
    drawsOfficial: "Les seuils et catégories évoluent régulièrement. Vérifie toujours les données officielles d’IRCC avant de déposer un profil.",
    liveSync: "Données en direct IRCC",
    refresh: "Actualiser en direct",
    refreshing: "Actualisation...",
    
    approachKicker: "La Méthode JNIC",
    founderTitle: "Comprendre avant d’agir.",
    founderText: "JNIC met à ta disposition de l’information générale, des outils pédagogiques et un accompagnement d’organisation pour t’aider à maîtriser les critères, préparer tes documents et garder le contrôle complet de ton projet.",
    
    whyKicker: "Pédagogie & Autonomie",
    whyTitle: "Une démarche conçue pour te rendre autonome",
    whySub: "Une approche bilingue, pédagogique et accessible aux francophones partout dans le monde.",
    
    testimonialKicker: "Résultats & Témoignages",
    testimonialTitle: "Témoignages : Leurs parcours vers la Résidence Permanente",
    testimonialSub: "Des retours authentiques de talents francophones ayant obtenu leur confirmation de résidence permanente (RP) au Canada avec JNIC.",
    
    ctaTitle: "Prêt(e) à connaître les options adaptées à ton profil ?",
    ctaSub: "Commence par un diagnostic gratuit ou réserve une séance d'orientation personnalisée de 30 minutes avec un conseiller.",
    learnMore: "En savoir plus",
    discover: "Découvrir la vision JNIC",
    book: "Réserver une consultation",
    online: "Session en ligne • 30 min",
    invitations: "invitations",
    updated: "Données synchronisées avec les tirages officiels d’IRCC.",
    officialSource: "Consulter la source officielle IRCC",
    disclaimer: "JNIC est un organisme d'information, de formation linguistique et d'accompagnement organisationnel. JNIC ne garantit pas l'émission de visas qui relève exclusivement de la compétence souveraine du gouvernement du Canada.",
  },
  en: {
    badgeTop: "🇨🇦 Express Entry · Francophone Priority Category",
    eyebrow: "Canadian pathways for French-speaking talent",
    title1: "Graduate?",
    title2: "Do you speak French?",
    title3: "Canada is looking for you.",
    sub: "Explore immigration, study and work pathways tailored to your profile — wherever you live — with verified information, a structured method and human support without false promises.",
    primary: "Take my free TCF assessment",
    secondary: "Explore all 7 programs",
    badge: "Verifiable information • Remote support",
    trust: "From the French-speaking world to Canada, with an understandable, transparent process and no unrealistic promises.",
    
    // Pathway finder widget
    finderTitle: "Find your Canadian pathway in 2 clicks",
    finderSubtitle: "Select your current profile and target goal:",
    finderProfileLabel: "1. Your profile:",
    finderGoalLabel: "2. Your goal:",
    finderResultLabel: "Recommended pathway:",
    finderAdvantageLabel: "Strategic advantage:",
    
    servicesKicker: "JNIC Programs & Services",
    servicesTitle: "Start with the pathway that fits your plan",
    servicesSub: "A clear selection of the most requested pathways: permanent residence, TCF training, higher education and work mobility.",
    
    resultsKicker: "Results & Experience",
    resultsTitle: "Real journeys supported with structure",
    resultsSub: "These figures reflect JNIC’s real experience. Every file is unique and treated with diligence.",
    
    drawsKicker: "Express Entry · IRCC Thresholds",
    drawsTitle: "Why French is the decisive key",
    drawsSub: "Compare recent CRS score thresholds: the French category provides a massive advantage with accessible scores (often over 100 points lower than general draws).",
    drawsOfficial: "Scores and categories evolve continuously. Always verify official IRCC data before submitting a profile.",
    liveSync: "Live IRCC Data",
    refresh: "Refresh Live",
    refreshing: "Refreshing...",
    
    approachKicker: "The JNIC Approach",
    founderTitle: "Understand before you act.",
    founderText: "JNIC provides general guidance, educational tools and organizational support to help you understand criteria, prepare documentation and stay in control of your project.",
    
    whyKicker: "Pedagogy & Autonomy",
    whyTitle: "A process designed to make you autonomous",
    whySub: "A bilingual, educational approach available to French speakers anywhere in the world.",
    
    testimonialKicker: "Client Testimonials",
    testimonialTitle: "Testimonials: Journeys to Permanent Residence",
    testimonialSub: "Authentic stories from Francophone talents who achieved their Canadian Permanent Residence (PR) with JNIC.",
    
    ctaTitle: "Ready to explore the right pathway for you?",
    ctaSub: "Start with a free assessment or book a 30-minute one-on-one strategic consultation with an advisor.",
    learnMore: "Learn more",
    discover: "Discover the JNIC vision",
    book: "Book a consultation",
    online: "Online session • 30 min",
    invitations: "invitations",
    updated: "Synchronized with official IRCC invitation rounds.",
    officialSource: "View official IRCC source",
    disclaimer: "JNIC provides educational information, language training and organizational support. Visa issuance remains under the sole authority of the Government of Canada.",
  },
};

const services = {
  fr: [
    {
      icon: BriefcaseBusiness,
      title: "Résidence Permanente",
      badge: "Priorité IRCC",
      badgeColor: "bg-red-50 text-red-700 border-red-200",
      text: "Entrée Express, sélection par catégorie, calcul optimisé du score CRS et audit de profil.",
      points: ["Bonus Francophone (+50 pts)", "Programme FSW / CEC", "Équivalences WES / ICAS"],
      anchor: "pr",
      link: "/services#pr",
    },
    {
      icon: Languages,
      title: "TCF Canada & NCLC 7+",
      badge: "Indispensable",
      badgeColor: "bg-amber-50 text-amber-800 border-amber-200",
      text: "Diagnostic initial, entraînement intensif aux 4 épreuves et stratégies pour viser le niveau C1.",
      points: ["Compréhension orale & écrite", "Simulation expression orale", "Roadmap personnalisée"],
      anchor: "tcf",
      link: "/tcf",
    },
    {
      icon: GraduationCap,
      title: "Études au Canada",
      badge: "Tremplin Direct",
      badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
      text: "Choix de l'université ou collège, CAQ, preuve de capacité financière et permis d’études.",
      points: ["Sélection des programmes DLI", "Lettre d'explication solide", "Accompagnement arrivée"],
      anchor: "student",
      link: "/services#student",
    },
    {
      icon: FileCheck2,
      title: "Mobilité Francophone",
      badge: "Exemption EIMT",
      badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
      text: "Permis de travail avec dispense d'EIMT pour candidats bilingues hors Québec.",
      points: ["Mobilité Francophone (C16)", "Registre des talents bilingues", "Mise en relation employeurs"],
      anchor: "work",
      link: "/registry",
    },
  ],
  en: [
    {
      icon: BriefcaseBusiness,
      title: "Permanent Residence",
      badge: "IRCC Priority",
      badgeColor: "bg-red-50 text-red-700 border-red-200",
      text: "Express Entry, category-based selection, CRS score optimization and comprehensive file audit.",
      points: ["French Category Bonus (+50 pts)", "FSW / CEC Streams", "WES / ICAS ECA Guidance"],
      anchor: "pr",
      link: "/services#pr",
    },
    {
      icon: Languages,
      title: "TCF Canada & CLB 7+",
      badge: "Essential",
      badgeColor: "bg-amber-50 text-amber-800 border-amber-200",
      text: "Initial diagnostic, intensive 4-module training and targeted strategies for CLB 7 to 9.",
      points: ["Listening & Reading tests", "Speaking mock interviews", "Custom study roadmap"],
      anchor: "tcf",
      link: "/tcf",
    },
    {
      icon: GraduationCap,
      title: "Study in Canada",
      badge: "Direct Pathway",
      badgeColor: "bg-blue-50 text-blue-700 border-blue-200",
      text: "University/college selection, CAQ, proof of financial support and study permit submission.",
      points: ["Designated Learning Institutions", "Solid statement of purpose", "Pre-arrival readiness"],
      anchor: "student",
      link: "/services#student",
    },
    {
      icon: FileCheck2,
      title: "Francophone Mobility",
      badge: "LMIA-Exempt",
      badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
      text: "LMIA-exempt work permit stream for bilingual professionals outside Quebec.",
      points: ["Mobility stream (C16)", "Bilingual talent registry", "Canadian employer alignment"],
      anchor: "work",
      link: "/registry",
    },
  ],
};

const advantages = {
  fr: [
    {
      icon: ShieldCheck,
      title: "Information 100% Vérifiée",
      text: "Nous fondons chaque recommandation sur les directives et lois officielles d’Immigration, Réfugiés et Citoyenneté Canada (IRCC).",
    },
    {
      icon: Search,
      title: "Audit Rigoureux du Profil",
      text: "Analyse objective de ton âge, diplômes, expérience professionnelle et niveau de langue avant toute démarche financière.",
    },
    {
      icon: BookOpen,
      title: "Méthode Pédagogique & Autonomie",
      text: "Tu comprends chaque formulaire et chaque document pour maîtriser ton propre dossier de A à Z.",
    },
    {
      icon: MessageCircle,
      title: "Accompagnement Humain & Direct",
      text: "Échanges réguliers en visioconférence et par WhatsApp avec un conseiller dédié qui suit ton évolution.",
    },
  ],
  en: [
    {
      icon: ShieldCheck,
      title: "100% Verified Information",
      text: "Every recommendation is grounded in official guidelines from Immigration, Refugees and Citizenship Canada (IRCC).",
    },
    {
      icon: Search,
      title: "Rigorous Profile Audit",
      text: "Objective analysis of your age, education, work experience and language levels before committing financial resources.",
    },
    {
      icon: BookOpen,
      title: "Educational & Autonomous Method",
      text: "You understand every form and supporting document to master your own file from start to finish.",
    },
    {
      icon: MessageCircle,
      title: "Direct & Human Support",
      text: "Regular video calls and dedicated WhatsApp communication with an advisor who tracks your progress.",
    },
  ],
};

const stats = {
  fr: [
    { value: "14", label: "Candidats accompagnés", icon: UsersRound, highlight: "Dossiers réels" },
    { value: "100%", label: "Visas de Résident permanent obtenus", icon: BadgeCheck, highlight: "100% de réussite" },
    { value: "382 - 456", label: "Scores des candidats accompagnés", icon: TrendingUp, highlight: "Entrée Express" },
    { value: "2", label: "Pays accompagnés (Cameroun & Côte d'Ivoire)", icon: Globe2, highlight: "Afrique subsaharienne" },
  ],
  en: [
    { value: "14", label: "Candidates accompanied", icon: UsersRound, highlight: "Real files" },
    { value: "100%", label: "Permanent Resident visas obtained", icon: BadgeCheck, highlight: "100% success rate" },
    { value: "382 - 456", label: "Candidate CRS scores range", icon: TrendingUp, highlight: "Express Entry" },
    { value: "2", label: "Countries accompanied (Cameroon & Ivory Coast)", icon: Globe2, highlight: "Sub-Saharan Africa" },
  ],
};

const testimonials = {
  fr: [
    {
      name: "Josué M.",
      origin: "Douala, Cameroun 🇨🇲 → Ontario 🇨🇦",
      tag: "Auto-démarche guidée · Entrée Express",
      score: "Score SCG : 398 · RP Obtenue",
      quote:
        "Après des devis exorbitants chez des intermédiaires, j’ai préféré gérer moi-même mon dossier avec l'accompagnement pédagogique de JNIC depuis Douala. Leurs conseils méthodologiques pour le TCF Canada et la clarté sur les critères m'ont permis de soumettre mon dossier en toute autonomie, d'obtenir mon invitation avec 398 points et mon visa de Résident permanent.",
    },
    {
      name: "Marcelle & Patrick T.",
      origin: "Yaoundé, Cameroun 🇨🇲 → Nouveau-Brunswick 🇨🇦",
      tag: "Procédure familiale en autonomie",
      score: "Score SCG : 442 · RP Obtenue",
      quote:
        "Nous voulions piloter nous-mêmes notre procédure sans passer par un représentant légal payé une fortune. Le coaching stratégique de JNIC nous a guidés pas à pas : optimisation du TCF, équivalence des diplômes WES et calcul des points de couple. Nous avons monté et soumis notre dossier nous-mêmes jusqu'à l'obtention de la RP pour nous et nos 2 enfants.",
    },
    {
      name: "Dr. Samuel E.",
      origin: "Bafoussam / Douala, Cameroun 🇨🇲 → Alberta 🇨🇦",
      tag: "Autonomie complète · Catégorie Francophone",
      score: "Score SCG : 418 · RP Obtenue",
      quote:
        "JNIC m'a apporté le cadre éducatif et la stratégie dont j'avais besoin pour comprendre les rouages du portail IRCC et cibler les tirages francophones. J'ai constitué, vérifié et soumis l'intégralité de ma demande moi-même. Résultat : confirmation e-COPR et visa obtenus avec 418 points, sans intermédiaire.",
    },
  ],
  en: [
    {
      name: "Josué M.",
      origin: "Douala, Cameroon 🇨🇲 → Ontario 🇨🇦",
      tag: "Guided Self-Processing · Express Entry",
      score: "CRS Score: 398 · PR Obtained",
      quote:
        "After receiving exorbitant quotes from agencies, I chose to self-manage my application with JNIC's educational guidance from Douala. Their strategic coaching on TCF Canada and transparent criteria allowed me to submit my file independently, get invited with 398 points, and secure my Permanent Resident visa.",
    },
    {
      name: "Marcelle & Patrick T.",
      origin: "Yaoundé, Cameroon 🇨🇲 → New Brunswick 🇨🇦",
      tag: "Self-Managed Family Application",
      score: "CRS Score: 442 · PR Obtained",
      quote:
        "We wanted to lead our own immigration process without paying huge fees to intermediaries. JNIC's coaching guided us step-by-step: TCF strategy, WES assessments, and couples points optimization. We prepared and submitted our entire file ourselves, achieving PR for our family with 2 children.",
    },
    {
      name: "Dr. Samuel E.",
      origin: "Bafoussam / Douala, Cameroon 🇨🇲 → Alberta 🇨🇦",
      tag: "Full Autonomy · Francophone Category",
      score: "CRS Score: 418 · PR Obtained",
      quote:
        "JNIC provided the educational roadmap and strategy I needed to understand the IRCC portal and target French-category draws. I personally prepared, reviewed, and submitted my application myself. Result: e-COPR and visa obtained with 418 CRS points, with zero middlemen.",
    },
  ],
};

function formatDrawDate(dateStr, lang) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function Home() {
  const { lang } = useLang();
  const c = content[lang] || content.fr;
  const [draws, setDraws] = useState(FALLBACK_DRAWS);
  const [drawsLoading, setDrawsLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // Interactive Pathway Finder State
  const [selectedProfile, setSelectedProfile] = useState("graduate");
  const [selectedGoal, setSelectedGoal] = useState("pr");

  const pathwayRecommendations = {
    graduate_pr: {
      title: lang === "fr" ? "Entrée Express — Catégorie Francophone" : "Express Entry — French Category",
      advantage: lang === "fr" ? "+50 points CRS automatiques avec TCF NCLC 7+" : "+50 automatic CRS points with CLB 7+",
      ctaText: lang === "fr" ? "Simuler mon score TCF" : "Simulate my TCF score",
      ctaLink: "/tcf/diagnostic",
      timeline: "6 - 9 mois",
    },
    graduate_study: {
      title: lang === "fr" ? "Permis d'Études (DLI) avec Volet Direct" : "Study Permit (DLI) with Direct Stream",
      advantage: lang === "fr" ? "Diplôme canadien + Permis de travail post-diplôme (PTPD)" : "Canadian degree + Post-Graduation Work Permit (PGWP)",
      ctaText: lang === "fr" ? "Préparer mes études" : "Plan my studies",
      ctaLink: "/services#student",
      timeline: "4 - 8 mois",
    },
    graduate_work: {
      title: lang === "fr" ? "Mobilité Francophone (Exemption EIMT)" : "Francophone Mobility (LMIA Exempt)",
      advantage: lang === "fr" ? "Permis de travail rapide hors Québec sans démarche EIMT complexe" : "Fast-track work permit outside Quebec without LMIA",
      ctaText: lang === "fr" ? "Rejoindre le registre talents" : "Join Talent Registry",
      ctaLink: "/registry",
      timeline: "2 - 5 mois",
    },
    couple_pr: {
      title: lang === "fr" ? "Entrée Express Conjointe & Optimisation Époux" : "Dual Express Entry & Spouse Optimization",
      advantage: lang === "fr" ? "Maximisation des points du conjoint (TCF + Diplômes + Expérience)" : "Spouse points maximization (TCF + Education + Work)",
      ctaText: lang === "fr" ? "Calculer le profil couple" : "Evaluate Couple Profile",
      ctaLink: "/registry/couples",
      timeline: "6 - 12 mois",
    },
    couple_study: {
      title: lang === "fr" ? "Études + Permis de Travail Ouvert Conjoint" : "Studies + Open Work Permit for Spouse",
      advantage: lang === "fr" ? "L'un étudie, le conjoint travaille à temps plein au Canada" : "One studies, the spouse works full-time in Canada",
      ctaText: lang === "fr" ? "Consulter les options familiales" : "View Family Options",
      ctaLink: "/services#student",
      timeline: "5 - 9 mois",
    },
    couple_work: {
      title: lang === "fr" ? "Mobilité Francophone & Visa Famille" : "Francophone Mobility & Family Visas",
      advantage: lang === "fr" ? "Permis de travail pour le travailleur + ouvert pour l'époux" : "Work permit for worker + open permit for spouse",
      ctaText: lang === "fr" ? "Réserver un bilan couple" : "Book Couple Assessment",
      ctaLink: "/booking",
      timeline: "3 - 6 mois",
    },
    worker_pr: {
      title: lang === "fr" ? "Programme FSW / CEC avec Bonus Linguistique" : "FSW / CEC Program with Language Bonus",
      advantage: lang === "fr" ? "Valorisation de l'expérience professionnelle qualifiée (FÉER 0, 1, 2)" : "Direct points for skilled experience (TEER 0, 1, 2)",
      ctaText: lang === "fr" ? "Tester mon éligibilité" : "Check Eligibility",
      ctaLink: "/tcf/diagnostic",
      timeline: "6 - 10 mois",
    },
    worker_work: {
      title: lang === "fr" ? "Mobilité Francophone & Candidatures Ciblées" : "Francophone Mobility & Targeted Job Matches",
      advantage: lang === "fr" ? "Processus employeur simplifié grâce à la maîtrise du français" : "Simplified employer process via French proficiency",
      ctaText: lang === "fr" ? "Déposer mon CV bilingue" : "Submit Bilingual CV",
      ctaLink: "/registry",
      timeline: "2 - 4 mois",
    },
    worker_study: {
      title: lang === "fr" ? "Reconversion & Perfectionnement Canadien" : "Canadian Upskilling & Second Career",
      advantage: lang === "fr" ? "Certificats post-diplôme (1 à 2 ans) pour insertion directe" : "Post-graduate certificates (1-2 yrs) for direct hire",
      ctaText: lang === "fr" ? "Explorer les programmes" : "Explore Programs",
      ctaLink: "/services#student",
      timeline: "4 - 8 mois",
    },
  };

  const currentRecommendation =
    pathwayRecommendations[`${selectedProfile}_${selectedGoal}`] ||
    pathwayRecommendations.graduate_pr;

  const fetchLiveDraws = async (triggerSync = false) => {
    setDrawsLoading(true);
    try {
      if (triggerSync) {
        try {
          await api.post("/express-entry/sync");
        } catch {
          // Ignore if sync network fails, fallback to DB fetch
        }
      }
      const response = await api.get("/express-entry/draws?limit=6");
      if (Array.isArray(response.data) && response.data.length > 0) {
        setDraws(response.data);
        setLastSyncTime(new Date());
      }
    } catch {
      // Keep fallback draws
    } finally {
      setDrawsLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveDraws();
  }, []);

  return (
    <div className="bg-[#F8FAFC] overflow-hidden" data-testid="home-page">
      {/* HERO SECTION */}
      <section className="relative min-h-[850px] lg:min-h-[92vh] bg-[#06162A] text-white flex flex-col justify-between pt-6 pb-12">
        {/* Ambient Backdrops */}
        <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
        <div className="absolute top-10 left-1/4 w-[500px] h-[500px] rounded-full bg-[#D71920]/15 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-[600px] h-[600px] rounded-full bg-[#D7A33D]/10 blur-[160px] pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 items-center gap-12 my-auto">
          {/* Left Column: Value Proposition */}
          <div className="lg:col-span-7 z-10 pt-4 lg:pt-0">
            {/* Live Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-[#D7A33D]/40 bg-white/5 px-4 py-1.5 text-xs font-semibold text-[#F2CA68] backdrop-blur-md shadow-xs"
            >
              <span className="w-2 h-2 rounded-full bg-[#D71920] animate-ping" />
              <span>{c.badgeTop}</span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="mt-6 font-serif text-4xl sm:text-6xl xl:text-[80px] leading-[0.94] tracking-[-0.03em] max-w-3xl"
            >
              <span className="block text-white/90">{c.title1}</span>
              <span className="block text-white font-medium">{c.title2}</span>
              <span className="block mt-2 text-[#EF4444] font-bold drop-shadow-sm">
                {c.title3}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-white/75"
            >
              {c.sub}
            </motion.p>

            {/* Primary Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.6 }}
              className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
            >
              <Link
                to="/tcf/diagnostic"
                id="hero-diagnostic-cta"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-[#D71920] text-white px-7 py-4 text-sm font-bold shadow-[0_10px_30px_rgba(215,25,32,0.4)] hover:bg-[#B91219] hover:shadow-[0_14px_40px_rgba(215,25,32,0.5)] active:scale-98 transition-all group"
              >
                <Sparkles size={18} className="text-[#F2CA68]" />
                <span>{c.primary}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>

              <Link
                to="/booking"
                id="hero-booking-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 hover:bg-white/10 hover:border-white/40 text-white px-6 py-4 text-sm font-semibold backdrop-blur-sm transition-all group"
              >
                <CalendarDays size={18} className="text-[#D7A33D]" />
                <span>{c.book}</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform text-white/70" />
              </Link>
            </motion.div>

            {/* Key Trust Points */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-x-7 gap-y-3 text-xs sm:text-sm text-white/70"
            >
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#D7A33D]" />
                {lang === "fr" ? "Francophones partout dans le monde" : "French speakers worldwide"}
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#D7A33D]" />
                {lang === "fr" ? "Accompagnement 100% à distance" : "100% remote guidance"}
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-[#D7A33D]" />
                {lang === "fr" ? "Bilingue Français • Anglais" : "Bilingual French • English"}
              </span>
            </motion.div>
          </div>

          {/* Right Column: Interactive Quick Pathway Recommender */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="glass-card p-6 sm:p-7 relative overflow-hidden border border-white/20 shadow-2xl"
            >
              <div className="flex items-center justify-between gap-2 pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#D71920]/20 text-[#EF4444] flex items-center justify-center">
                    <Zap size={18} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-white leading-tight">
                      {c.finderTitle}
                    </h3>
                    <p className="text-[11px] text-white/60">{c.finderSubtitle}</p>
                  </div>
                </div>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-[#D7A33D]/20 text-[#F2CA68] border border-[#D7A33D]/30">
                  Interactif
                </span>
              </div>

              {/* Step 1: Profile Selector */}
              <div className="mt-5">
                <label className="block text-xs font-semibold text-white/80 mb-2">
                  {c.finderProfileLabel}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "graduate", label: lang === "fr" ? "Diplômé(e)" : "Graduate", icon: GraduationCap },
                    { id: "worker", label: lang === "fr" ? "Professionnel" : "Professional", icon: BriefcaseBusiness },
                    { id: "couple", label: lang === "fr" ? "En couple" : "Couple / Family", icon: UsersRound },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setSelectedProfile(item.id)}
                      className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-xs font-medium transition-all ${
                        selectedProfile === item.id
                          ? "bg-[#D71920] border-[#EF4444] text-white shadow-md"
                          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <item.icon size={16} className="mb-1" />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2: Goal Selector */}
              <div className="mt-4">
                <label className="block text-xs font-semibold text-white/80 mb-2">
                  {c.finderGoalLabel}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "pr", label: lang === "fr" ? "Résidence Perm." : "Perm. Residence", icon: ShieldCheck },
                    { id: "work", label: lang === "fr" ? "Travailler" : "Work Permit", icon: FileCheck2 },
                    { id: "study", label: lang === "fr" ? "Étudier" : "Study in Canada", icon: BookOpen },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setSelectedGoal(item.id)}
                      className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-xs font-medium transition-all ${
                        selectedGoal === item.id
                          ? "bg-[#D7A33D] border-[#F2CA68] text-[#06162A] font-bold shadow-md"
                          : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <item.icon size={16} className="mb-1" />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Result Preview Box */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${selectedProfile}_${selectedGoal}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="mt-5 p-4 rounded-xl bg-black/40 border border-white/15"
                >
                  <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                    <span>{c.finderResultLabel}</span>
                    <span className="text-[#F2CA68] font-mono flex items-center gap-1">
                      <Clock size={11} /> {currentRecommendation.timeline}
                    </span>
                  </div>
                  <h4 className="font-serif text-lg font-bold text-white">
                    {currentRecommendation.title}
                  </h4>
                  <p className="text-xs text-white/75 mt-1">
                    <b className="text-[#F2CA68]">{c.finderAdvantageLabel}</b> {currentRecommendation.advantage}
                  </p>

                  <Link
                    to={currentRecommendation.ctaLink}
                    className="mt-3.5 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-white/15 hover:bg-white/25 text-white text-xs font-bold transition border border-white/20"
                  >
                    <span>{currentRecommendation.ctaText}</span>
                    <ArrowRight size={14} />
                  </Link>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </div>

        {/* Trust Banner / Ticker */}
        <div className="relative border-t border-white/10 bg-black/30 backdrop-blur-md mt-6">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-3 text-white/80">
              <span className="font-serif text-base text-[#F2CA68] font-bold">JNIC</span>
              <span>•</span>
              <span>{c.trust}</span>
            </div>
            <div className="flex items-center gap-4 text-white/60">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-emerald-400" />
                Démarche Éthique & Sans Frais Cachés
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS & IMPACT BAR */}
      <section className="py-12 bg-white border-b border-slate-200/80">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(stats[lang] || stats.fr).map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="stat-card"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-[#06162A] text-[#D7A33D] flex items-center justify-center">
                    <item.icon size={20} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {item.highlight}
                  </span>
                </div>
                <p className="font-serif text-4xl sm:text-5xl font-bold text-[#06162A] mt-4 leading-none">
                  {item.value}
                </p>
                <p className="mt-2 text-xs sm:text-sm text-slate-600 font-medium">
                  {item.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EXPRESS ENTRY DRAWS TRACKER (REAL-TIME COMPARISON) */}
      <section className="py-20 lg:py-24 bg-[#EFF6FA]" data-testid="ee-draws-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-xs font-bold uppercase tracking-widest text-[#D71920]">
                  {c.drawsKicker}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {c.liveSync}
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#06162A]">
                {c.drawsTitle}
              </h2>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <p className="text-slate-600 text-sm max-w-md leading-relaxed">
                {c.drawsSub}
              </p>
              <button
                onClick={() => fetchLiveDraws(true)}
                disabled={drawsLoading}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition shadow-xs disabled:opacity-60 shrink-0"
                title={c.refresh}
              >
                <RotateCw size={14} className={drawsLoading ? "animate-spin text-[#D71920]" : "text-slate-500"} />
                <span>{drawsLoading ? c.refreshing : c.refresh}</span>
              </button>
            </div>
          </div>

          {/* Draw Cards Grid */}
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {draws.slice(0, 3).map((draw, index) => {
              const isFrench =
                draw.isFrench ||
                String(draw.category).toLowerCase().includes("french") ||
                String(draw.category_fr).toLowerCase().includes("français");
              const category =
                lang === "fr" ? draw.category_fr || draw.category : draw.category || draw.category_fr;

              return (
                <motion.article
                  key={draw.draw_number || index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border transition-all ${
                    isFrench
                      ? "border-[#D71920]/40 ring-2 ring-[#D71920]/15 shadow-md"
                      : "border-slate-200"
                  }`}
                  data-testid={`ee-draw-${draw.draw_number}`}
                >
                  {isFrench && (
                    <div className="absolute top-0 right-0 bg-[#D71920] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-bl-xl shadow-xs">
                      🔥 Priorité Francophone
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-slate-500 mb-4 pt-1">
                    <span className="font-mono font-bold text-slate-700">
                      {lang === "fr" ? "Tirage #" : "Round #"}{draw.draw_number}
                    </span>
                    <span className="font-medium text-slate-600">{formatDrawDate(draw.date, lang)}</span>
                  </div>

                  <span
                    className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md mb-4 ${
                      isFrench
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {category}
                  </span>

                  <div className="my-3">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-5xl font-bold text-[#06162A]">
                        {draw.crs}
                      </span>
                      <span className="text-xs font-bold tracking-wider text-slate-500 uppercase">
                        {lang === "fr" ? "Points CRS" : "CRS Points"}
                      </span>
                    </div>
                    {isFrench ? (
                      <p className="text-xs font-semibold text-emerald-600 mt-1 flex items-center gap-1">
                        <TrendingUp size={13} /> {lang === "fr" ? "Seuil accessible avec NCLC 7 au TCF" : "Accessible score with TCF CLB 7"}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-500 mt-1">
                        {lang === "fr" ? "Seuil élevé réservé aux profils bilingues ou avec offre" : "High cutoff requiring bilingual profile or job offer"}
                      </p>
                    )}
                  </div>

                  <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                    <span>{lang === "fr" ? "Invitations émises (ITAs) :" : "Invitations issued (ITAs):"}</span>
                    <b className="font-mono text-sm text-[#06162A]">
                      {Number(draw.itas || 0).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                    </b>
                  </div>
                </motion.article>
              );
            })}
          </div>

          {/* Action Row */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-slate-200/80">
            <a
              href={lang === "fr" ? IRCC_CRS_TOOL_URL_FR : IRCC_CRS_TOOL_URL_EN}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#D71920] hover:text-[#B91219] transition group"
            >
              <span>
                {lang === "fr"
                  ? "Tester si mon score actuel atteint ce seuil (Outil SCG officiel IRCC)"
                  : "Test if my score reaches this cutoff (Official IRCC CRS Tool)"}
              </span>
              <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>

            <a
              href={IRCC_DRAWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-[#06162A] transition"
            >
              <span>{c.officialSource}</span>
              <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </section>

      {/* CORE SERVICES & PROGRAMMES GRID */}
      <section className="py-24 bg-white" id="programmes">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#D71920]">
              {c.servicesKicker}
            </span>
            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#06162A] mt-2">
              {c.servicesTitle}
            </h2>
            <p className="text-slate-600 text-base mt-3 leading-relaxed">
              {c.servicesSub}
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(services[lang] || services.fr).map((srv, index) => (
              <motion.div
                key={srv.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="premium-card flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#06162A] text-[#D7A33D] flex items-center justify-center group-hover:scale-105 transition-transform">
                      <srv.icon size={24} />
                    </div>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${srv.badgeColor}`}>
                      {srv.badge}
                    </span>
                  </div>

                  <h3 className="font-serif text-2xl font-bold text-[#06162A]">
                    {srv.title}
                  </h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                    {srv.text}
                  </p>

                  <ul className="mt-5 space-y-2 text-xs text-slate-700">
                    {srv.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-2">
                        <CheckCircle2 size={13} className="text-[#D7A33D] shrink-0" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to={srv.link}
                  className="mt-8 pt-4 border-t border-slate-100 inline-flex items-center justify-between text-xs font-bold text-[#D71920] group-hover:text-[#B91219] transition"
                >
                  <span>{c.learnMore}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-[#06162A] hover:border-[#D71920] hover:text-[#D71920] transition shadow-xs"
            >
              <span>{lang === "fr" ? "Consulter l'ensemble de nos 7 services d'immigration" : "View all 7 immigration services"}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* METHODOLOGY & PHILOSOPHY */}
      <section className="py-24 bg-[#F8FAFC] border-y border-slate-200/70">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5 relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] border border-white">
              <img
                src={BLACK_PROFESSIONALS_IMAGE}
                alt="Talents francophones d'Afrique subsaharienne au Canada"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06162A]/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-[#D71920] text-white p-6 rounded-2xl shadow-xl max-w-[260px]">
              <p className="font-serif text-3xl font-bold">A → Z</p>
              <p className="text-xs text-white/90 mt-1 font-medium">
                {lang === "fr" ? "Un projet maîtrisé, sans intermédiaire opaque." : "A master file, without opaque middlemen."}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-6">
            <span className="text-xs font-bold uppercase tracking-widest text-[#D71920]">
              {c.approachKicker}
            </span>
            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#06162A] mt-2">
              {c.founderTitle}
            </h2>
            <p className="text-slate-600 text-base mt-4 max-w-2xl leading-relaxed">
              {c.founderText}
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {(advantages[lang] || advantages.fr).map(({ icon: Icon, title, text }) => (
                <div
                  key={title}
                  className="p-5 rounded-2xl bg-white border border-slate-200/80 shadow-xs"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#06162A] text-[#D7A33D] flex items-center justify-center mb-3">
                    <Icon size={20} />
                  </div>
                  <h4 className="font-bold text-sm text-[#06162A]">{title}</h4>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-sm font-bold text-[#06162A] hover:text-[#D71920] transition"
              >
                <span>{c.discover}</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-[#D71920]">
              {c.testimonialKicker}
            </span>
            <h2 className="font-serif text-3xl sm:text-5xl font-bold text-[#06162A] mt-2">
              {c.testimonialTitle}
            </h2>
            <p className="text-slate-600 text-base mt-3">{c.testimonialSub}</p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {(testimonials[lang] || testimonials.fr).map((t, index) => (
              <article
                key={t.name}
                className="testimonial-card flex flex-col justify-between"
                data-testid={`testimonial-${index + 1}`}
              >
                <div>
                  <Quote size={32} className="text-[#D7A33D] mb-4" />
                  <p className="font-serif text-lg leading-relaxed text-[#06162A]">
                    “{t.quote}”
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-sm text-[#06162A]">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.origin}</p>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                      {t.score}
                    </span>
                  </div>
                  <p className="text-[11px] font-semibold text-[#D71920] mt-1.5">
                    {t.tag}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL HIGH-IMPACT CALL TO ACTION */}
      <section className="px-4 sm:px-6 lg:px-10 pb-20 bg-white">
        <div className="max-w-[1400px] mx-auto relative overflow-hidden rounded-3xl bg-[#06162A] text-white p-8 sm:p-14 lg:p-20 shadow-2xl">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#D71920]/25 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-80 h-80 rounded-full bg-[#D7A33D]/15 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F2CA68]">
                <CalendarDays size={16} />
                {lang === "fr" ? "Diagnostic & Orientation Stratégique" : "Strategic Assessment & Guidance"}
              </span>
              <h2 className="font-serif text-3xl sm:text-5xl font-bold mt-4 leading-tight">
                {c.ctaTitle}
              </h2>
              <p className="mt-4 max-w-2xl text-white/70 text-base sm:text-lg leading-relaxed">
                {c.ctaSub}
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-3">
              <Link
                to="/booking"
                id="footer-cta-booking"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#D71920] text-white px-8 py-4 text-sm font-bold shadow-lg hover:bg-[#B91219] transition-all group"
              >
                <span>{c.book}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <span className="text-xs text-white/50 flex items-center gap-1.5">
                <MapPin size={13} /> {c.online}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
